import { AddCircleOutline, RemoveCircleOutline, Close, Save, Publish } from '@mui/icons-material';
import {
  Alert,
  Box,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { toInputDateFormat } from '../../lib/utils';
import { createReleaseNote, updateReleaseNote } from '../../modules/admin';
import { RELEASE_NOTE_ITEM_ALLOWED_TYPES } from '../../static/common';
import ConfirmationModal from '../../ui/primitives/ConfirmationModal';
import Modal from '../../ui/layout/Modal';
import CustomInputField from '../../ui/primitives/CustomInputField';
import { FIELD_STYLES } from '../../ui/primitives/fieldStyles';
import ActionButton from '../../ui/primitives/ActionButton';
import { useNotification } from '../../hooks/NotificationContext';

const defaultItem = { type: '', title: '', description: '' };
const currentDate = new Date().toISOString().split('T')[0];

const defaultValues = {
  version: 'v',
  title: '',
  releasedAt: currentDate,
  published: false,
  description: '',
  items: [defaultItem],
};

const CreateReleaseNoteModal = ({
  showModal,
  handleClose,
  refreshList,
  initialData,
  mode,
}) => {
  const isEditMode = mode === 'edit' ? true : false;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const isPublished = initialData?.published;
  const { addNotification } = useNotification();

  const loadInitialValues = useCallback(() => {
    if (isEditMode && initialData) {
      setInitialValues({
        version: initialData.version?.startsWith('v')
          ? initialData.version
          : `v${initialData.version || ''}`,
        title: initialData.title || '',
        releasedAt: toInputDateFormat(initialData.released_at) || currentDate,
        published: initialData.published || false,
        description: initialData.description || '',
        items: initialData.items?.length ? initialData.items : [defaultItem],
      });
    } else {
      setInitialValues(defaultValues);
    }
  }, [isEditMode, initialData]);

  useEffect(() => {
    if (showModal) loadInitialValues();
  }, [showModal, loadInitialValues]);

  const handleSave = async (values, shouldPublish, resetForm) => {
    const filteredItems = values.items.filter(
      (item) => item.type?.trim() && item.title?.trim()
    );
    const payload = {
      ...values,
      items: filteredItems,
      published: shouldPublish,
    };
    try {
      isEditMode
        ? await updateReleaseNote(initialData.id, payload)
        : await createReleaseNote(payload);
      addNotification('success', 'Release note updated successfully!');
      refreshList?.();
      resetForm();
      handleDiscard();
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to update release note.');
    }
  };

  const handleDiscard = () => {
    setShowConfirmationModal(false);
    handleClose();
    setUploadError(null);
    setInitialValues(defaultValues);
  };

  if (!initialValues) return null;

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={() => {}}>
        {({ values, setFieldValue, setValues, isSubmitting, resetForm }) => (
          <Modal
            open={showModal}
            width={'lg'}
            handleClose={() => setShowConfirmationModal(true)}
            modalTitle={
              isEditMode ? 'Edit Release Note' : 'Create Release Note'
            }
            action={() => (
              <>
                <ActionButton
                  label="Close"
                  icon={<Close />}
                  intent="secondary"
                  onClick={() => setShowConfirmationModal(true)}
                  disabled={isSubmitting}
                />
                <ActionButton
                  label="Save as Draft"
                  icon={<Save />}
                  intent="secondary"
                  onClick={() => handleSave(values, false, resetForm)}
                  disabled={isSubmitting || isPublished}
                />
                <ActionButton
                  label={isSubmitting ? 'Saving...' : 'Save & Publish'}
                  icon={<Publish />}
                  intent="primary"
                  loading={isSubmitting}
                  onClick={() => handleSave(values, true, resetForm)}
                  disabled={
                    !/^v[0-9A-Za-z].*/.test(values.version?.trim() || '') ||
                    !values.title?.trim() ||
                    isSubmitting
                  }
                />
              </>
            )}>
            <Form>
              {!isEditMode && (
                <>
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <InputLabel>
                      Upload JSON File (
                      <a href={'/sampleReleaseNote.json'} download>
                        Download format
                      </a>
                      )
                    </InputLabel>
                    <input
                      type="file"
                      accept=".json"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        const reader = new FileReader();
                        reader.onload = (event) => {
                          try {
                            const json = JSON.parse(event.target.result);

                            const isValid =
                              typeof json.version === 'string' &&
                              typeof json.title === 'string' &&
                              typeof json.releasedAt === 'string' &&
                              Array.isArray(json.items) &&
                              json.items.every(
                                (item) =>
                                  typeof item.type === 'string' &&
                                  typeof item.title === 'string' &&
                                  (typeof item.description === 'string' ||
                                    item.description === undefined)
                              );

                            if (!isValid) {
                              setUploadError(
                                'This file does not have a valid structure. Please upload a valid JSON file.'
                              );
                              return;
                            }

                            setValues({
                              version: json.version.startsWith('v')
                                ? json.version
                                : `v${json.version}`,
                              title: json.title,
                              releasedAt: json.releasedAt || currentDate,
                              published: json.published || false,
                              description: json.description || '',
                              items: json.items.length
                                ? json.items
                                : [defaultItem],
                            });
                            setUploadError(null);
                          } catch (err) {
                            console.error(err);
                            setUploadError('Unable to parse JSON file.');
                          }
                        };
                        reader.readAsText(file);
                      }}
                    />
                    {uploadError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {uploadError}
                      </Alert>
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                </>
              )}

              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                Release Details
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomInputField
                    id="version"
                    label="Version"
                    required
                    value={values.version}
                    onChange={(id, value) => {
                      const updated = value.startsWith('v')
                        ? value
                        : `v${value.replace(/^v*/, '')}`;
                      setFieldValue('version', updated);
                    }}
                    placeholder="e.g. v1.2.3"
                    maxLength={25}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack spacing={0.25}>
                    <InputLabel
                      required
                      sx={{
                        position: 'static',
                        transform: 'none',
                        mb: 0.75,
                        '& .MuiFormLabel-asterisk': { color: 'error.main' },
                      }}>
                      Release Date
                    </InputLabel>
                    <TextField
                      type="date"
                      id="releasedAt"
                      value={values.releasedAt}
                      onChange={(e) =>
                        setFieldValue('releasedAt', e.target.value)
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      disabled={values?.published}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: FIELD_STYLES.minHeight,
                          boxSizing: 'border-box',
                          backgroundColor: 'transparent',
                        },
                        '& .MuiOutlinedInput-input': {
                          height: '100%',
                          boxSizing: 'border-box',
                          fontSize: FIELD_STYLES.fontSize,
                          py: 0,
                          px: FIELD_STYLES.paddingX,
                          border: '0 !important',
                          outline: 'none',
                          backgroundColor: 'transparent !important',
                          backgroundImage: 'none !important',
                          boxShadow: 'none !important',
                          borderRadius: 0,
                          WebkitAppearance: 'none',
                          MozAppearance: 'textfield',
                          appearance: 'none',
                          colorScheme: 'light',
                          '&::-webkit-datetime-edit': { padding: 0 },
                          '&::-webkit-datetime-edit-fields-wrapper': { padding: 0 },
                          '&::-webkit-inner-spin-button': { display: 'none' },
                          '&::-webkit-clear-button': { display: 'none' },
                          '&::-webkit-calendar-picker-indicator': {
                            cursor: 'pointer',
                            opacity: 0.6,
                          },
                        },
                      }}
                    />
                  </Stack>
                </Box>
                {/*
                  Note: native <input type="date"> control chrome differs per
                  browser (esp. Firefox vs Chromium) and can't always be fully
                  reset with CSS. If the nested-box look persists in a given
                  browser, replace this with a non-native date field (e.g. an
                  MUI X DatePicker) instead of an <input type="date">.
                */}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <CustomInputField
                    id="title"
                    label="Title"
                    required
                    value={values.title}
                    onChange={(id, value) => setFieldValue(id, value)}
                    maxLength={50}
                    placeholder="Release Title"
                  />
                </Grid>
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <CustomInputField
                    id="description"
                    label="Description"
                    value={values.description}
                    onChange={(id, value) => setFieldValue(id, value)}
                    maxLength={255}
                    placeholder="Release description"
                    multiline
                    rows={4}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FieldArray name="items">
                    {({ push, remove }) => (
                      <Stack spacing={2}>
                        {RELEASE_NOTE_ITEM_ALLOWED_TYPES.map(
                          (type, sectionIdx) => {
                            const itemsForType = values.items
                              .map((item, i) => ({ ...item, index: i }))
                              .filter((item) => item.type === type);

                            return (
                              <Box key={sectionIdx}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  sx={{ mb: 1 }}>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold">
                                    {type}
                                  </Typography>
                                  <Tooltip title={`Add ${type}`}>
                                    <IconButton
                                      color="primary"
                                      sx={{ ml: 2 }}
                                      onClick={() =>
                                        push({
                                          type,
                                          title: '',
                                          description: '',
                                        })
                                      }>
                                      <AddCircleOutline fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Grid container spacing={2}>
                                  {itemsForType?.length === 0 ? (
                                    <Grid item xs={12}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontStyle="italic">
                                        No items added yet.
                                      </Typography>
                                    </Grid>
                                  ) : (
                                    itemsForType.map((item) => (
                                      <React.Fragment key={item.index}>
                                        <Grid item xs={4}>
                                          <CustomInputField
                                            id={`items[${item.index}].title`}
                                            value={item.title}
                                            onChange={(id, value) =>
                                              setFieldValue(id, value)
                                            }
                                            multiline
                                            rows={2}
                                            placeholder={`${type} Title`}
                                            maxLength={50}
                                          />
                                        </Grid>
                                        <Grid item xs={7}>
                                          <CustomInputField
                                            id={`items[${item.index}].description`}
                                            value={item.description}
                                            onChange={(id, value) =>
                                              setFieldValue(id, value)
                                            }
                                            placeholder={`${type} Description`}
                                            maxLength={150}
                                            multiline
                                            rows={2}
                                          />
                                        </Grid>
                                        <Grid
                                          item
                                          xs={1}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}>
                                          <IconButton
                                            color="error"
                                            title="Remove"
                                            onClick={() => remove(item.index)}>
                                            <RemoveCircleOutline fontSize="small" />
                                          </IconButton>
                                        </Grid>
                                      </React.Fragment>
                                    ))
                                  )}
                                </Grid>
                              </Box>
                            );
                          }
                        )}
                      </Stack>
                    )}
                  </FieldArray>
                </Grid>
              </Grid>
            </Form>
          </Modal>
        )}
      </Formik>

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        handleConfirm={handleDiscard}
        title="Discard Changes?"
        body="Are you sure you want to cancel? Unsaved changes will be lost."
        buttonLabel="Discard"
        buttonLabelSecondary="Go Back"
        buttonStyle="primary"
      />
    </>
  );
};

export default CreateReleaseNoteModal;